/**
 * Silently upgrades the user to the latest frontend assets when a new backend
 * version is detected (via VERSION_MISMATCH_EVENT, fired by the axios
 * interceptor). Renders nothing — there is no banner.
 *
 * We deploy multiple times a day, so a persistent "please refresh" banner was
 * nagging. Instead we refresh at the least disruptive moment, never mid-task:
 *  1. On the next client-side navigation — the page the user lands on is clean.
 *  2. When the tab becomes hidden (user switched away / minimized) — reloads in
 *     the background so a screen with live/polling data is fresh on return.
 *  3. After the user has been idle on the current page for IDLE_REFRESH_MS —
 *     catches a polling screen the user walked away from without navigating.
 *
 * (2) and (3) are skipped while any form has unsaved changes (tracked via the
 * unsaved-changes registry) to avoid a surprise "Leave site?" prompt or data
 * loss. (1) is always safe because the destination page has no in-flight edits.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { VERSION_MISMATCH_EVENT } from '../api/axiosInstance';
import { isCapacitor } from '../api/config';
import { hasUnsavedChanges } from '../utils/unsavedChangesRegistry';

/** Idle time (no user interaction) before we silently refresh in place. */
export const IDLE_REFRESH_MS = 2 * 60 * 1000;

const INTERACTION_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
] as const;

async function clearCachesAndReload() {
  // Clear all caches and unregister service workers so the browser fetches the
  // new assets rather than serving stale ones.
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  }

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));
  }

  window.location.reload();
}

export function VersionChecker() {
  const location = useLocation();
  const currentPath = useRef(location.pathname);
  const updatePending = useRef(false);
  const pathAtDetection = useRef<string | null>(null);
  const reloaded = useRef(false);

  // Track the latest route path so the (stable) detection handler can record
  // where the user was when the update was detected.
  useEffect(() => {
    currentPath.current = location.pathname;
  }, [location.pathname]);

  const safeReload = useCallback((requireClean: boolean) => {
    if (reloaded.current) return;
    // On Capacitor the app runs from bundled local assets, so reloading cannot
    // fetch a new build — updates ship via the OTA flow (apply-on-next-launch).
    // A mid-session reload would just disrupt the user without updating anything.
    if (isCapacitor) return;
    if (requireClean && hasUnsavedChanges()) return;
    reloaded.current = true;
    void clearCachesAndReload();
  }, []);

  // Detect a pending update. Record the path we were on so we don't count the
  // navigation that may have triggered detection as "the next navigation".
  useEffect(() => {
    const handleVersionMismatch = () => {
      updatePending.current = true;
      pathAtDetection.current = currentPath.current;
    };

    window.addEventListener(VERSION_MISMATCH_EVENT, handleVersionMismatch);
    return () => {
      window.removeEventListener(VERSION_MISMATCH_EVENT, handleVersionMismatch);
    };
  }, []);

  // Trigger 1: refresh on the next client-side navigation.
  useEffect(() => {
    if (
      updatePending.current &&
      pathAtDetection.current !== null &&
      location.pathname !== pathAtDetection.current
    ) {
      safeReload(false);
    }
  }, [location.pathname, safeReload]);

  // Triggers 2 & 3: tab hidden, or foreground idle.
  useEffect(() => {
    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (updatePending.current) safeReload(true);
      }, IDLE_REFRESH_MS);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        if (updatePending.current) safeReload(true);
      } else {
        resetIdleTimer();
      }
    };

    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, resetIdleTimer, { passive: true })
    );
    document.addEventListener('visibilitychange', handleVisibility);
    resetIdleTimer();

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, resetIdleTimer));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [safeReload]);

  return null;
}
